import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteWorkspace,
  getWorkspace,
  createWorkspace,
  updateWorkspace,
  getCurrentWorkspace,
  getAllWorkspaceTerminologies,
} from "../Api/workspace";
import { getAllWorkspaces } from "../Api/workspace";

export const useWorkspaces = (page: number, rowsPerPage: number) => {
  return useQuery({
    queryKey: ["workspaces", page, rowsPerPage],
    queryFn: () => getAllWorkspaces(page, rowsPerPage),
  });
};

export const useWorkspace = (workspaceId: string | null) => {
  return useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => getWorkspace(workspaceId!),
    enabled: !!workspaceId,
  });
};

export const useCurrentWorkspace = () => {
  return useQuery({
    queryKey: ["currentWorkspace"],
    queryFn: () => getCurrentWorkspace(),
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      description,
      userEvaluationLimit,
      userCompanyLimit,
      userStorageLimit,
      userLibraryLimit,
      namespaces,
      iconImage,
      workspaceAdminId,
      features,
      certifications,
      model_ids,
      experts,
      primary_color,
      secondary_color,
      accent_color,
      terminology,
      alan_credentials_id,
      language,
      show_locked_features,
      agent_ids,
    }: {
      name: string;
      description: string;
      userEvaluationLimit: number;
      userCompanyLimit: number;
      userStorageLimit: number;
      userLibraryLimit: number;
      namespaces: string[];
      iconImage: File | null;
      workspaceAdminId: number | null;
      features: number[];
      certifications: number[];
      model_ids: number[];
      experts: string[];
      primary_color: string | null;
      secondary_color: string | null;
      accent_color: string | null;
      terminology: number;
      alan_credentials_id: number | null;
      language: number;
      show_locked_features: boolean;
      agent_ids?: number[];
    }) =>
      createWorkspace(
        name,
        description,
        userEvaluationLimit,
        userCompanyLimit,
        userStorageLimit,
        userLibraryLimit,
        namespaces,
        iconImage,
        workspaceAdminId,
        features,
        certifications,
        model_ids,
        experts,
        primary_color,
        secondary_color,
        accent_color,
        terminology,
        alan_credentials_id,
        language,
        show_locked_features,
        agent_ids || [],
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      name,
      description,
      userEvaluationLimit,
      userCompanyLimit,
      userStorageLimit,
      userLibraryLimit,
      namespaces,
      iconImage,
      workspaceAdminId,
      features,
      certifications,
      model_ids,
      experts,
      primary_color,
      secondary_color,
      accent_color,
      terminology,
      alan_credentials_id,
      language,
      show_locked_features,
      agent_ids,
    }: {
      workspaceId: number;
      name: string;
      description: string;
      userEvaluationLimit: number;
      userCompanyLimit: number;
      userStorageLimit: number;
      userLibraryLimit: number;
      namespaces: string[];
      iconImage: File | null;
      workspaceAdminId: number | null;
      features: number[];
      certifications: number[];
      model_ids: number[];
      experts: string[];
      primary_color: string | null;
      secondary_color: string | null;
      accent_color: string | null;
      terminology: number;
      alan_credentials_id: number | null;
      language: number;
      show_locked_features: boolean;
      agent_ids?: number[];
    }) =>
      updateWorkspace(
        workspaceId,
        name,
        description,
        userEvaluationLimit,
        userCompanyLimit,
        userStorageLimit,
        userLibraryLimit,
        namespaces,
        iconImage,
        workspaceAdminId,
        features,
        certifications,
        model_ids,
        experts,
        primary_color,
        secondary_color,
        accent_color,
        terminology,
        alan_credentials_id,
        language,
        show_locked_features,
        agent_ids || [],
      ),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      queryClient.invalidateQueries({
        queryKey: ["workspace", variables.workspaceId.toString()],
      });
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: number) => deleteWorkspace(workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });
};

export const useWorkspaceTerminologies = () => {
  return useQuery({
    queryKey: ["workspaceTerminologies"],
    queryFn: () => getAllWorkspaceTerminologies(),
  });
};
